import { adminApiUrl, authedFetch } from "./lib/api";
import type { MediaItem } from "./media";

const PICKER_API = "https://photospicker.googleapis.com/v1";
const PHOTOS_TOKEN_HEADER = "X-Cat-Photos-Token";
const PHOTOS_RECONNECT_HEADER = "X-Cat-Photos-Reconnect";
const MAX_ERROR_DETAIL_LENGTH = 2000;
const MAX_ERROR_DETAIL_BYTES = 4000;
const OVERSIZED_ERROR_DETAIL = "Response detail was too large to display.";
export const PICKER_SCOPE = "https://www.googleapis.com/auth/photospicker.mediaitems.readonly";

export interface GooglePhotosToken {
  accessToken: string;
  expiresAt: number;
}

export interface PickerSession {
  id: string;
  pickerUri: string;
  mediaItemsSet?: boolean;
  pollingConfig?: { pollInterval?: string; timeoutIn?: string };
}

export interface PickedMediaItem {
  id: string;
  createTime?: string;
  mediaFile?: {
    baseUrl?: string;
    filename?: string;
    mimeType?: string;
    mediaFileMetadata?: { videoMetadata?: { processingStatus?: string } };
  };
}

export interface ImportedMediaItem extends MediaItem {
  skipped: boolean;
}

export class PickerRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly photosReconnectRequired = false,
  ) {
    super(message);
  }
}

export function shouldStopImport(error: unknown): boolean {
  return error instanceof PickerRequestError
    && (error.status === 0 || error.status === 401 || error.status === 429);
}

export function shouldReconnectPhotos(error: unknown): boolean {
  return error instanceof PickerRequestError && error.photosReconnectRequired;
}

async function pickerError(
  response: Response,
  action: string,
  reconnectOnUnauthorized = false,
): Promise<PickerRequestError> {
  const declaredLength = Number(response.headers.get("content-length"));
  let detail: string;
  try {
    if (Number.isFinite(declaredLength) && declaredLength > MAX_ERROR_DETAIL_BYTES) {
      await response.body?.cancel().catch(() => undefined);
      detail = OVERSIZED_ERROR_DETAIL;
    } else {
      detail = await boundedResponseDetail(response);
    }
  } catch {
    detail = "Response detail was unavailable.";
  }
  return new PickerRequestError(
    `${action} failed (${response.status})${detail ? `: ${detail}` : ""}`,
    response.status,
    response.headers.get(PHOTOS_RECONNECT_HEADER) === "true"
      || (response.status === 401 && reconnectOnUnauthorized),
  );
}

async function boundedResponseDetail(response: Response): Promise<string> {
  if (!response.body) return "";
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let received = 0;
  let detail = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) return (detail + decoder.decode()).slice(0, MAX_ERROR_DETAIL_LENGTH);
    received += value.byteLength;
    if (received > MAX_ERROR_DETAIL_BYTES) {
      await reader.cancel().catch(() => undefined);
      return OVERSIZED_ERROR_DETAIL;
    }
    detail += decoder.decode(value, { stream: true });
  }
}

export function durationMs(value: string | undefined, fallback: number): number {
  if (!value?.endsWith("s")) return fallback;
  const seconds = Number.parseFloat(value.slice(0, -1));
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : fallback;
}

export function requestGooglePhotosToken(): Promise<GooglePhotosToken> {
  const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId || !window.google?.accounts?.oauth2) {
    return Promise.reject(new Error("Google Photos is not configured yet."));
  }

  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: PICKER_SCOPE,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error_description || response.error || "Google Photos connection failed."));
          return;
        }
        if (!window.google!.accounts.oauth2.hasGrantedAllScopes(response, PICKER_SCOPE)) {
          reject(new Error("Google Photos permission was not granted."));
          return;
        }
        const expiresIn = Number.parseInt(response.expires_in ?? "3600", 10);
        resolve({
          accessToken: response.access_token,
          expiresAt: Date.now() + Math.max(1, expiresIn) * 1000,
        });
      },
      error_callback: (error) => reject(new Error(error.message || error.type || "Google Photos connection failed.")),
    });
    client.requestAccessToken();
  });
}

export async function createPickerSession(token: string): Promise<PickerSession> {
  const response = await fetch(`${PICKER_API}/sessions`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ pickingConfig: { maxItemCount: "50" } }),
  });
  if (!response.ok) throw await pickerError(response, "Creating picker session", true);
  return response.json() as Promise<PickerSession>;
}

export async function pollPickerSession(
  token: string,
  session: PickerSession,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<PickerSession> {
  const interval = Math.max(1000, durationMs(session.pollingConfig?.pollInterval, 3000));
  const timeout = durationMs(session.pollingConfig?.timeoutIn, 300000);
  const attempts = Math.max(1, Math.ceil(timeout / interval));

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    await wait(interval);
    const response = await fetch(`${PICKER_API}/sessions/${encodeURIComponent(session.id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw await pickerError(response, "Checking picker session", true);
    const current = await response.json() as PickerSession;
    if (current.mediaItemsSet) return current;
  }
  throw new Error("Google Photos selection timed out. Try again.");
}

export async function listPickedMedia(token: string, sessionId: string): Promise<PickedMediaItem[]> {
  const items: PickedMediaItem[] = [];
  let pageToken: string | undefined;
  do {
    const query = new URLSearchParams({ sessionId });
    if (pageToken) query.set("pageToken", pageToken);
    const response = await fetch(`${PICKER_API}/mediaItems?${query}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw await pickerError(response, "Listing selected media", true);
    const page = await response.json() as { mediaItems?: PickedMediaItem[]; nextPageToken?: string };
    items.push(...(page.mediaItems ?? []));
    pageToken = page.nextPageToken;
  } while (pageToken);
  return items;
}

export async function deletePickerSession(token: string, sessionId: string): Promise<void> {
  await fetch(`${PICKER_API}/sessions/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function importPickedMedia(item: PickedMediaItem, token: string): Promise<ImportedMediaItem> {
  const mediaFile = item.mediaFile;
  if (!mediaFile?.baseUrl || !mediaFile.filename || !mediaFile.mimeType) {
    throw new Error("Google Photos returned an incomplete media item.");
  }
  const processingStatus = mediaFile.mediaFileMetadata?.videoMetadata?.processingStatus;
  if (mediaFile.mimeType.startsWith("video/") && processingStatus && processingStatus !== "READY") {
    throw new Error("Video is still processing in Google Photos.");
  }

  let response: Response;
  try {
    response = await authedFetch(adminApiUrl("/api/admin/google-photos/import"), {
      method: "POST",
      headers: { "Content-Type": "application/json", [PHOTOS_TOKEN_HEADER]: token },
      body: JSON.stringify({
        id: item.id,
        base_url: mediaFile.baseUrl,
        filename: mediaFile.filename,
        mime_type: mediaFile.mimeType,
      }),
    });
  } catch {
    throw new PickerRequestError("Importing media failed: Could not reach Cat Network.", 0);
  }
  if (!response.ok) throw await pickerError(response, "Importing media");
  return response.json() as Promise<ImportedMediaItem>;
}
